import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import api from "../api";
import { clearAuthToken, readAuthToken, writeAuthToken } from "../authStorage";

type EnterpriseSummary = {
  id: number;
  name: string;
};

type AppUser = {
  id: number;
  name: string;
  email: string;
  role: string;
  enterprise_id?: number | null;
  is_active: boolean;
};

type Role = {
  id: number;
  key: string;
  name: string;
  description: string;
  is_system: boolean;
};

type UserRoleAssignment = {
  id: number;
  user_id: number;
  role_id: number;
  scope: string;
  scope_id?: string | null;
  created_at: string;
};

type RoleAssignmentDraft = {
  role_key: string;
  scope: string;
  scope_id: string;
};

type OwnershipSummary = {
  kpis: string[];
  initiatives: string[];
  tasks: string[];
};

type WorkspaceSummary = {
  availableLenses?: string[];
  lensConfigs?: Record<string, { name?: string; description?: string }>;
};

const brandLogoUrl = `${process.env.PUBLIC_URL}/favicon.png?v=2`;
const loginIntroVideoUrl = `${process.env.PUBLIC_URL}/lenses-login-intro.mp4`;
const loginIntroReplayParam = "intro";
const authUserStorageKey = "lenses.authUser";

const cacheAuthUser = (user: AppUser) => {
  window.localStorage.setItem(authUserStorageKey, JSON.stringify(user));
};

const clearAuthSession = () => {
  clearAuthToken();
  window.localStorage.removeItem(authUserStorageKey);
};

const safeNextPath = (value: string | null) => {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return "/";
  return value;
};

const fallbackRoles: Role[] = [
  { id: 0, key: "super_admin", name: "Super Admin", description: "Global unrestricted access.", is_system: true },
  { id: 0, key: "enterprise_admin", name: "Enterprise Admin", description: "Full control inside one enterprise.", is_system: true },
  { id: 0, key: "workspace_manager", name: "Workspace Manager", description: "Full control inside assigned workspaces.", is_system: true },
  { id: 0, key: "kpi_owner", name: "KPI Owner", description: "Controls owned KPIs and descendant work.", is_system: true },
  { id: 0, key: "contributor", name: "Contributor", description: "Updates assigned work.", is_system: true },
  { id: 0, key: "viewer", name: "Viewer", description: "Read-only access.", is_system: true },
];
const assignmentScopes = ["enterprise", "lens", "access_tag", "global"];
const scopesWithoutIds = new Set(["global"]);
const accessTagOptions = [
  { key: "financial", label: "Financial" },
  { key: "confidential", label: "Confidential" },
  { key: "giving", label: "Giving" },
  { key: "payroll", label: "Payroll" },
  { key: "budget", label: "Budget" },
];

const emptyForm = {
  name: "",
  email: "",
  password: "",
  enterprise_id: "",
};

type UserEditDraft = {
  name: string;
  email: string;
  enterprise_id: string;
  is_active: boolean;
  password: string;
};

const roleScopeHint = (roleKey: string) => {
  switch (roleKey) {
    case "super_admin":
      return "Use global scope.";
    case "enterprise_admin":
      return "Usually scoped to one enterprise.";
    case "workspace_manager":
      return "Scope to the workspace they manage.";
    case "kpi_owner":
      return "Usually comes from KPI owner fields; ownership is shown below each user.";
    case "contributor":
      return "Best for assigned tasks, updates, evidence, and comments.";
    case "viewer":
      return "Use the narrowest scope they should be able to read.";
    default:
      return "Assign only where this role should apply.";
  }
};

const scopeDescriptions: Record<string, string> = {
  global: "Every enterprise and workspace.",
  enterprise: "One enterprise and every lens the role can view there.",
  lens: "One lens inside one enterprise.",
  access_tag: "Sensitive field access inside one enterprise.",
  owned: "Automatic: KPIs, initiatives, and tasks where this user is the owner.",
  assigned: "Automatic: tasks or work directly assigned to the user.",
};

const emptyOwnershipSummary: OwnershipSummary = {
  kpis: [],
  initiatives: [],
  tasks: [],
};

const normalizeOwnerKey = (value?: string | null) => value?.trim().toLowerCase() ?? "";

const addUnique = (items: string[], value: string) => {
  const nextValue = value.trim();
  if (!nextValue || items.includes(nextValue)) return;
  items.push(nextValue);
};

const describeOwnership = (summary?: OwnershipSummary) => {
  if (!summary) return "";
  const parts = [
    summary.kpis.length ? `${summary.kpis.length} KPI${summary.kpis.length === 1 ? "" : "s"}` : "",
    summary.initiatives.length ? `${summary.initiatives.length} initiative${summary.initiatives.length === 1 ? "" : "s"}` : "",
    summary.tasks.length ? `${summary.tasks.length} task${summary.tasks.length === 1 ? "" : "s"}` : "",
  ].filter(Boolean);
  return parts.join(", ");
};

const hasWorkspaceOwnership = (summary?: OwnershipSummary) =>
  Boolean(summary && (summary.kpis.length > 0 || summary.initiatives.length > 0 || summary.tasks.length > 0));

const parseLensScopeId = (scopeId?: string | null) => {
  if (!scopeId) return { enterpriseId: "", lensId: "" };
  const [enterpriseId, ...lensParts] = scopeId.split(":");
  if (lensParts.length === 0) return { enterpriseId: "", lensId: scopeId };
  return { enterpriseId, lensId: lensParts.join(":") };
};

const buildLensScopeId = (enterpriseId: string, lensId: string) =>
  enterpriseId && lensId ? `${enterpriseId}:${lensId}` : "";

const parseAccessTagScopeId = (scopeId?: string | null) => {
  if (!scopeId) return { enterpriseId: "", tag: "" };
  const [enterpriseId, ...tagParts] = scopeId.split(":");
  if (tagParts.length === 0) return { enterpriseId: "", tag: scopeId };
  return { enterpriseId, tag: tagParts.join(":") };
};

const buildAccessTagScopeId = (enterpriseId: string, tag: string) =>
  enterpriseId && tag ? `${enterpriseId}:${tag}` : "";

const summarizeWorkspaceOwnership = (users: AppUser[], workspaces: unknown[]) => {
  const ownerLookup = new Map<string, number>();
  users.forEach((user) => {
    ownerLookup.set(normalizeOwnerKey(user.name), user.id);
    ownerLookup.set(normalizeOwnerKey(user.email), user.id);
  });

  const nextSummaries: Record<number, OwnershipSummary> = Object.fromEntries(
    users.map((user) => [user.id, { kpis: [], initiatives: [], tasks: [] }])
  );

  const addOwnerItem = (
    owner: unknown,
    type: keyof OwnershipSummary,
    label: string
  ) => {
    if (typeof owner !== "string") return;
    const userId = ownerLookup.get(normalizeOwnerKey(owner));
    if (!userId) return;
    addUnique(nextSummaries[userId][type], label);
  };

  workspaces.forEach((workspace) => {
    if (!workspace || typeof workspace !== "object") return;
    const allNodes = (workspace as { allNodes?: unknown }).allNodes;
    if (!Array.isArray(allNodes)) return;

    allNodes.forEach((node) => {
      if (!node || typeof node !== "object") return;
      const data = (node as { data?: Record<string, unknown> }).data;
      if (!data) return;

      const kpiLabel = typeof data.label === "string" ? data.label : "Untitled KPI";
      addOwnerItem(data.owner, "kpis", kpiLabel);

      const initiatives = data.initiatives;
      if (Array.isArray(initiatives)) {
        initiatives.forEach((initiative) => {
          if (!initiative || typeof initiative !== "object") return;
          const nextInitiative = initiative as Record<string, unknown>;
          const initiativeTitle = typeof nextInitiative.title === "string" ? nextInitiative.title : "Untitled initiative";
          addOwnerItem(nextInitiative.owner, "initiatives", `${initiativeTitle} (${kpiLabel})`);

          const tasks = nextInitiative.tasks;
          if (Array.isArray(tasks)) {
            tasks.forEach((task) => {
              if (!task || typeof task !== "object") return;
              const nextTask = task as Record<string, unknown>;
              const taskTitle = typeof nextTask.title === "string" ? nextTask.title : "Untitled task";
              addOwnerItem(nextTask.owner, "tasks", `${taskTitle} (${initiativeTitle})`);
            });
          }
        });
      }

      const tasks = data.tasks;
      if (Array.isArray(tasks)) {
        tasks.forEach((task) => {
          if (!task || typeof task !== "object") return;
          const nextTask = task as Record<string, unknown>;
          const taskTitle = typeof nextTask.title === "string" ? nextTask.title : "Untitled task";
          addOwnerItem(nextTask.owner, "tasks", `${taskTitle} (${kpiLabel})`);
        });
      }
    });
  });

  return nextSummaries;
};

export default function Users() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = React.useState(() => readAuthToken());
  const [currentUser, setCurrentUser] = React.useState<AppUser | null>(null);
  const [users, setUsers] = React.useState<AppUser[]>([]);
  const [enterprises, setEnterprises] = React.useState<EnterpriseSummary[]>([]);
  const [roles, setRoles] = React.useState<Role[]>(fallbackRoles);
  const [roleAssignments, setRoleAssignments] = React.useState<Record<number, UserRoleAssignment[]>>({});
  const [assignmentDrafts, setAssignmentDrafts] = React.useState<Record<number, RoleAssignmentDraft>>({});
  const [ownershipByUserId, setOwnershipByUserId] = React.useState<Record<number, OwnershipSummary>>({});
  const [workspacesByEnterpriseId, setWorkspacesByEnterpriseId] = React.useState<Record<number, WorkspaceSummary>>({});
  const [expandedOwnershipUserIds, setExpandedOwnershipUserIds] = React.useState<Set<number>>(() => new Set());
  const [isRoleGuideExpanded, setIsRoleGuideExpanded] = React.useState(false);
  const [userSearch, setUserSearch] = React.useState("");
  const [loginEmail, setLoginEmail] = React.useState("");
  const [loginPassword, setLoginPassword] = React.useState("");
  const [form, setForm] = React.useState(emptyForm);
  const [editDrafts, setEditDrafts] = React.useState<Record<number, UserEditDraft>>({});
  const [message, setMessage] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const forceLoginIntro = searchParams.get(loginIntroReplayParam) === "1";
  const suppressLoginIntro = searchParams.get(loginIntroReplayParam) === "0";
  const [loginIntroDismissed, setLoginIntroDismissed] = React.useState(false);
  const [loginIntroFadingOut, setLoginIntroFadingOut] = React.useState(false);
  const loginIntroVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const loginScreenRef = React.useRef<HTMLElement | null>(null);
  const shouldShowLoginIntro =
    !currentUser &&
    !loginIntroDismissed &&
    !suppressLoginIntro &&
    (forceLoginIntro || !token);
  const getRedirectPath = React.useCallback(
    () => safeNextPath(searchParams.get("next")),
    [searchParams]
  );

  const finishLoginIntro = React.useCallback(() => {
    setLoginIntroFadingOut(true);
    window.setTimeout(() => {
      setLoginIntroDismissed(true);
      setLoginIntroFadingOut(false);
    }, 900);
  }, []);

  React.useEffect(() => {
    if (!shouldShowLoginIntro) return;
    setLoginIntroFadingOut(false);
    const video = loginIntroVideoRef.current;
    if (!video) return;
    const playPromise = video.play();
    if (playPromise) {
      playPromise.catch((error) => {
        console.warn("Login intro video autoplay was blocked", error);
      });
    }
  }, [shouldShowLoginIntro]);

  React.useEffect(() => {
    const loginScreen = loginScreenRef.current;
    if (!loginScreen) return;
    if (shouldShowLoginIntro) {
      loginScreen.setAttribute("inert", "");
      return;
    }
    loginScreen.removeAttribute("inert");
  }, [shouldShowLoginIntro]);

  const handleLoginIntroError = React.useCallback(() => {
    console.warn(`Login intro video failed to load from ${loginIntroVideoUrl}`);
    setLoginIntroDismissed(true);
  }, []);

  const explainError = (error: unknown, fallback: string) => {
    if (axios.isAxiosError(error)) {
      const detail = error.response?.data?.detail;
      if (typeof detail === "string" && detail.trim()) {
        return detail;
      }
    }
    return fallback;
  };

  const loadUsers = React.useCallback(async () => {
    if (!token) return;
    try {
      const meResponse = await api.get("/auth/me");
      setCurrentUser(meResponse.data as AppUser);
      cacheAuthUser(meResponse.data as AppUser);

      const [usersResponse, enterprisesResponse] = await Promise.all([
        api.get("/users/"),
        api.get("/enterprises/"),
      ]);
      const nextUsers = usersResponse.data as AppUser[];
      setUsers(nextUsers);
      setEditDrafts(
        Object.fromEntries(
          nextUsers.map((user) => [
            user.id,
            {
              name: user.name,
              email: user.email,
              enterprise_id: user.enterprise_id == null ? "" : String(user.enterprise_id),
              is_active: user.is_active,
              password: "",
            },
          ])
        )
      );
      setEnterprises(enterprisesResponse.data as EnterpriseSummary[]);
      const workspaceEntries = await Promise.all(
        (enterprisesResponse.data as EnterpriseSummary[]).map(async (enterprise) => {
          try {
            const response = await api.get(`/enterprises/${enterprise.id}/workspace`);
            return [enterprise.id, response.data?.workspace ?? null] as const;
          } catch (workspaceError) {
            console.error("Failed to load workspace ownership", workspaceError);
            return [enterprise.id, null] as const;
          }
        })
      );
      setWorkspacesByEnterpriseId(
        Object.fromEntries(
          workspaceEntries
            .filter((entry): entry is readonly [number, WorkspaceSummary] => Boolean(entry[1]))
            .map(([enterpriseId, workspace]) => [enterpriseId, workspace])
        )
      );
      setOwnershipByUserId(summarizeWorkspaceOwnership(nextUsers, workspaceEntries.map(([, workspace]) => workspace)));
      try {
        const rolesResponse = await api.get("/roles/");
        const nextRoles = rolesResponse.data as Role[];
        if (nextRoles.length > 0) {
          setRoles(nextRoles);
        }
      } catch (rolesError) {
        console.error("Failed to load roles", rolesError);
        setRoles(fallbackRoles);
      }
      const assignmentEntries = await Promise.all(
        nextUsers.map(async (user) => {
          try {
            const response = await api.get(`/users/${user.id}/role-assignments`);
            return [user.id, response.data as UserRoleAssignment[]] as const;
          } catch (assignmentError) {
            console.error("Failed to load role assignments", assignmentError);
            return [user.id, []] as const;
          }
        })
      );
      setRoleAssignments(Object.fromEntries(assignmentEntries));
      setAssignmentDrafts((current) => {
        const nextDrafts = { ...current };
        nextUsers.forEach((user) => {
          if (!nextDrafts[user.id]) {
            nextDrafts[user.id] = {
              role_key: "",
              scope: "",
              scope_id: "",
            };
          }
        });
        return nextDrafts;
      });
    } catch (error) {
      console.error("Failed to load users", error);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        clearAuthSession();
        setToken("");
        setCurrentUser(null);
        setMessage("Sign in to continue to your workspace.");
        return;
      }
      setMessage(explainError(error, "Could not load user permissions."));
    }
  }, [token]);

  React.useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const onLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setMessage(null);
    try {
      const response = await api.post("/auth/login", {
        email: loginEmail,
        password: loginPassword,
      });
      const nextToken = response.data.token as string;
      const nextUser = response.data.user as AppUser;
      writeAuthToken(nextToken);
      cacheAuthUser(nextUser);
      setToken(nextToken);
      setCurrentUser(nextUser);
      setLoginPassword("");
      setMessage("Signed in.");
      navigate(getRedirectPath(), { replace: true });
    } catch (error) {
      console.error("Failed to sign in", error);
      setMessage(explainError(error, "Could not sign in with that email and password."));
    } finally {
      setIsLoading(false);
    }
  };

  const onCreateUser = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setMessage(null);
    try {
      await api.post("/users/", {
        name: form.name,
        email: form.email,
        password: form.password,
        enterprise_id: form.enterprise_id ? Number(form.enterprise_id) : null,
      });
      setForm(emptyForm);
      if (token && currentUser) {
        setMessage("User saved.");
        await loadUsers();
        return;
      }

      const loginResponse = await api.post("/auth/login", {
        email: form.email,
        password: form.password,
      });
      const nextToken = loginResponse.data.token as string;
      const nextUser = loginResponse.data.user as AppUser;
      writeAuthToken(nextToken);
      cacheAuthUser(nextUser);
      setToken(nextToken);
      setCurrentUser(nextUser);
      setMessage("User saved and signed in.");
      await loadUsers();
      navigate(getRedirectPath(), { replace: true });
    } catch (error) {
      console.error("Failed to create user", error);
      setMessage(explainError(error, "Could not save this user. If this is not the first user, sign in as an owner/admin first."));
    } finally {
      setIsLoading(false);
    }
  };

  const updateDraft = (userId: number, patch: Partial<UserEditDraft>) => {
    setEditDrafts((current) => ({
      ...current,
      [userId]: {
        ...current[userId],
        ...patch,
      },
    }));
  };

  const updateAssignmentDraft = (userId: number, patch: Partial<RoleAssignmentDraft>) => {
    setAssignmentDrafts((current) => ({
      ...current,
      [userId]: {
        role_key: current[userId]?.role_key ?? "",
        scope: current[userId]?.scope ?? "",
        scope_id: current[userId]?.scope_id ?? "",
        ...patch,
      },
    }));
  };

  const onSaveUser = async (user: AppUser) => {
    const draft = editDrafts[user.id];
    if (!draft) return;

    setIsLoading(true);
    setMessage(null);
    try {
      await api.patch(`/users/${user.id}`, {
        name: draft.name,
        email: draft.email,
        enterprise_id: draft.enterprise_id ? Number(draft.enterprise_id) : null,
        is_active: draft.is_active,
        ...(draft.password ? { password: draft.password } : {}),
      });
      setMessage("User permissions saved.");
      await loadUsers();
    } catch (error) {
      console.error("Failed to update user", error);
      setMessage(explainError(error, "Could not save this user's permissions."));
    } finally {
      setIsLoading(false);
    }
  };

  const hasUnsavedUserChanges = (user: AppUser, draft?: UserEditDraft) => {
    if (!draft) return false;
    return (
      draft.name !== user.name ||
      draft.email !== user.email ||
      draft.enterprise_id !== (user.enterprise_id == null ? "" : String(user.enterprise_id)) ||
      draft.is_active !== user.is_active ||
      draft.password.trim().length > 0
    );
  };

  const onAddRoleAssignment = async (user: AppUser) => {
    const draft = assignmentDrafts[user.id] ?? {
      role_key: "",
      scope: "",
      scope_id: "",
    };
    if (!draft.role_key || !draft.scope) {
      setMessage("Choose a role and scope before adding permissions.");
      return;
    }
    const nextScopeId = scopesWithoutIds.has(draft.scope) ? null : draft.scope_id.trim() || null;
    if (draft.scope === "enterprise" && !nextScopeId) {
      setMessage("Choose an enterprise before adding enterprise permissions.");
      return;
    }
    if (draft.scope === "lens" && !nextScopeId) {
      setMessage("Choose an enterprise and lens before adding lens permissions.");
      return;
    }
    if (draft.scope === "access_tag" && !nextScopeId) {
      setMessage("Choose an enterprise and access tag before adding sensitive field permissions.");
      return;
    }
    setIsLoading(true);
    setMessage(null);
    try {
      await api.post("/role-assignments/", {
        user_id: user.id,
        role_key: draft.role_key,
        scope: draft.scope,
        scope_id: nextScopeId,
      });
      setMessage("Role assignment added.");
      await loadUsers();
    } catch (error) {
      console.error("Failed to add role assignment", error);
      setMessage(explainError(error, "Could not add role assignment."));
    } finally {
      setIsLoading(false);
    }
  };

  const onDeleteRoleAssignment = async (assignmentId: number) => {
    setIsLoading(true);
    setMessage(null);
    try {
      await api.delete(`/role-assignments/${assignmentId}`);
      setMessage("Role assignment removed.");
      await loadUsers();
    } catch (error) {
      console.error("Failed to remove role assignment", error);
      setMessage(explainError(error, "Could not remove role assignment."));
    } finally {
      setIsLoading(false);
    }
  };

  const onLogout = () => {
    if (token) {
      void api
        .post("/auth/logout", undefined, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .catch(() => undefined);
    }
    clearAuthSession();
    setToken("");
    setCurrentUser(null);
    setUsers([]);
    setRoleAssignments({});
    setOwnershipByUserId({});
    setExpandedOwnershipUserIds(new Set());
    setMessage("Signed out.");
  };

  const toggleOwnershipDetails = (userId: number) => {
    setExpandedOwnershipUserIds((current) => {
      const next = new Set(current);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const roleNameById = React.useMemo(
    () => new Map(roles.map((role) => [role.id, role.name])),
    [roles]
  );
  const roleKeyById = React.useMemo(
    () => new Map(roles.map((role) => [role.id, role.key])),
    [roles]
  );
  const roleDescriptionByKey = React.useMemo(
    () => new Map(roles.map((role) => [role.key, role.description])),
    [roles]
  );
  const assignableAccessRoles = React.useMemo(
    () => roles.filter((role) => role.key !== "kpi_owner"),
    [roles]
  );
  const enterpriseNameById = React.useMemo(
    () => new Map(enterprises.map((enterprise) => [String(enterprise.id), enterprise.name])),
    [enterprises]
  );
  const lensOptionsByEnterpriseId = React.useMemo(() => {
    const next: Record<string, Array<{ id: string; name: string }>> = {};
    Object.entries(workspacesByEnterpriseId).forEach(([enterpriseId, workspace]) => {
      const lensConfigs = workspace.lensConfigs ?? {};
      const availableLenses = workspace.availableLenses ?? Object.keys(lensConfigs);
      next[enterpriseId] = availableLenses.map((lensId) => ({
        id: lensId,
        name: lensConfigs[lensId]?.name || lensId,
      }));
    });
    return next;
  }, [workspacesByEnterpriseId]);
  const formatScope = React.useCallback((assignment: UserRoleAssignment) => {
    if (assignment.scope === "owned") return "owned work";
    if (assignment.scope === "assigned") return "assigned work";
    if (!assignment.scope_id) return assignment.scope;
    if (assignment.scope === "enterprise") {
      return enterpriseNameById.get(String(assignment.scope_id)) ?? `Enterprise ${assignment.scope_id}`;
    }
    if (assignment.scope === "lens") {
      const { enterpriseId, lensId } = parseLensScopeId(assignment.scope_id);
      const enterpriseLabel = enterpriseNameById.get(enterpriseId);
      const lensLabel = lensOptionsByEnterpriseId[enterpriseId]?.find((lens) => lens.id === lensId)?.name ?? lensId;
      return enterpriseLabel ? `${enterpriseLabel} / ${lensLabel}` : `Lens ${lensLabel}`;
    }
    if (assignment.scope === "access_tag") {
      const { enterpriseId, tag } = parseAccessTagScopeId(assignment.scope_id);
      const enterpriseLabel = enterpriseNameById.get(enterpriseId);
      const tagLabel = accessTagOptions.find((option) => option.key === tag)?.label ?? tag;
      return enterpriseLabel ? `${enterpriseLabel} / ${tagLabel} access` : `${tagLabel} access`;
    }
    return `${assignment.scope}:${assignment.scope_id}`;
  }, [enterpriseNameById, lensOptionsByEnterpriseId]);
  const filteredUsers = React.useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) => {
      const enterpriseName = user.enterprise_id == null ? "" : enterpriseNameById.get(String(user.enterprise_id)) ?? "";
      const ownership = describeOwnership(ownershipByUserId[user.id]);
      const grants = (roleAssignments[user.id] ?? [])
        .map((assignment) => `${roleNameById.get(assignment.role_id) ?? ""} ${formatScope(assignment)}`)
        .join(" ");
      return [
        user.name,
        user.email,
        user.is_active ? "active" : "inactive",
        enterpriseName,
        ownership,
        grants,
      ].join(" ").toLowerCase().includes(query);
    });
  }, [enterpriseNameById, formatScope, ownershipByUserId, roleAssignments, roleNameById, userSearch, users]);

  if (!currentUser) {
    const miniKpis = [
      { title: "Finance", target: "92%", actual: "88%", initiatives: 0, accent: "#facc15" },
      { title: "Customer Satisfaction", target: "90%", actual: "94%", initiatives: 1, accent: "#22c55e" },
      { title: "Teamwork", target: "85%", actual: "82%", initiatives: 2, accent: "#22c55e" },
      { title: "Innovation", target: "10", actual: "5", initiatives: 3, accent: "#f97316" },
    ];

    return (
      <>
        {shouldShowLoginIntro ? (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 20000,
              background: "#03070c",
              color: "#f8fafc",
              opacity: loginIntroFadingOut ? 0 : 1,
              transition: "opacity 900ms ease",
              pointerEvents: loginIntroFadingOut ? "none" : "auto",
              overflow: "hidden",
            }}
          >
            <video
              ref={loginIntroVideoRef}
              autoPlay
              muted
              playsInline
              preload="auto"
              onEnded={finishLoginIntro}
              onError={handleLoginIntroError}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                background: "#03070c",
              }}
            >
              <source src={loginIntroVideoUrl} type="video/mp4" />
            </video>
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(circle at 50% 50%, rgba(34,197,94,0.08), transparent 44%), linear-gradient(180deg, rgba(3,7,12,0.12), rgba(3,7,12,0.52))",
                pointerEvents: "none",
              }}
            />
            <button
              type="button"
              onClick={finishLoginIntro}
              style={{
                position: "absolute",
                right: 28,
                bottom: 28,
                border: "1px solid rgba(148,163,184,0.34)",
                borderRadius: 999,
                padding: "10px 16px",
                background: "rgba(8,13,20,0.72)",
                color: "#f8fafc",
                fontSize: 13,
                fontWeight: 900,
                cursor: "pointer",
                backdropFilter: "blur(10px)",
                boxShadow: "0 12px 28px rgba(0,0,0,0.3)",
              }}
            >
              Skip intro
            </button>
          </div>
        ) : null}
        <main
          ref={loginScreenRef}
          aria-hidden={shouldShowLoginIntro}
          style={{
            minHeight: "100vh",
            background:
              "radial-gradient(circle at 24% 20%, rgba(34,197,94,0.13), transparent 24%), radial-gradient(circle at 80% 80%, rgba(14,165,233,0.11), transparent 26%), linear-gradient(135deg, #05090f 0%, #081018 48%, #04070b 100%)",
            color: "#f8fafc",
            padding: "36px 28px",
            fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          }}
        >
        <div style={{ maxWidth: 1320, minHeight: "calc(100vh - 72px)", margin: "0 auto", display: "grid", gap: 24 }}>
          <section
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(420px, 100%), 1fr))",
              gap: 42,
              alignItems: "center",
            }}
          >
            <div
              style={{
                position: "relative",
                minHeight: 720,
                border: "1px solid rgba(148,163,184,0.12)",
                borderRadius: 22,
                padding: 40,
                overflow: "hidden",
                background:
                  "linear-gradient(rgba(148,163,184,0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.055) 1px, transparent 1px)",
                backgroundSize: "28px 28px",
              }}
            >
              <div style={{ position: "relative", zIndex: 1, display: "grid", gap: 16, maxWidth: 520 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <img
                    src={brandLogoUrl}
                    alt="Lenses logo"
                    style={{ width: 58, height: 58, objectFit: "contain", clipPath: "inset(3px 0 0 0)" }}
                  />
                  <div>
                    <div style={{ color: "#22c55e", fontSize: 12, fontWeight: 900, letterSpacing: "0.12em" }}>
                      STRATEGY OPERATING MODEL
                    </div>
                    <div style={{ fontSize: 34, fontWeight: 900, lineHeight: 1 }}>Lenses CPM</div>
                  </div>
                </div>
                <p style={{ margin: 0, color: "#aab4c2", fontSize: 15, lineHeight: 1.6, whiteSpace: "nowrap" }}>
                  Strategy, KPI ownership, initiatives, and task updates in one operating model.
                </p>
              </div>

              <div
                style={{
                  position: "relative",
                  zIndex: 2,
                  marginTop: 54,
                  maxWidth: 560,
                  padding: "18px 22px 20px 0",
                  background:
                    "linear-gradient(90deg, rgba(5,9,15,0.96) 0%, rgba(5,9,15,0.88) 74%, rgba(5,9,15,0.14) 100%)",
                }}
              >
                <h2 style={{ margin: 0, fontSize: 40, lineHeight: 1.12, letterSpacing: 0, fontWeight: 950 }}>
                  Keep strategy, measurement, and ownership{" "}
                  <span style={{ color: "#22c55e" }}>in view.</span>
                </h2>
                <p style={{ margin: "18px 0 0", color: "#aab4c2", fontSize: 15, lineHeight: 1.65, maxWidth: 470 }}>
                  Lenses connects KPIs, initiatives, tasks, evidence, and feedback so leadership conversations stay aligned to what matters.
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10, marginTop: 22, maxWidth: 520 }}>
                  {[
                    ["Strategic alignment", "#22c55e"],
                    ["Performance clarity", "#facc15"],
                    ["Task ownership", "#f97316"],
                  ].map(([label, color]) => (
                    <div
                      key={label}
                      style={{
                        border: "1px solid rgba(148,163,184,0.25)",
                        borderRadius: 8,
                        padding: "8px 12px",
                        background: "rgba(15,23,42,0.66)",
                        color,
                        fontSize: 13,
                        fontWeight: 900,
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </div>
                  ))}
                </div>
              </div>

              <div
                style={{
                  position: "absolute",
                  left: "calc(78% - 72px)",
                  top: 168,
                  width: 144,
                  border: "1px solid #facc15",
                  borderRadius: 10,
                  padding: 12,
                  background: "rgba(8,13,20,0.92)",
                  boxShadow: "0 0 24px rgba(250,204,21,0.14)",
                  filter: "blur(0.45px)",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 900 }}>Balanced Scorecard</div>
                <div style={{ marginTop: 8, color: "#8b97a8", fontSize: 10 }}>Weekly</div>
                <div style={{ marginTop: 8, border: "1px solid rgba(148,163,184,0.22)", borderRadius: 6, padding: "4px 6px", fontSize: 10 }}>Direct KPI</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12, fontSize: 10, color: "#8b97a8" }}>
                  <span>TARGET<br /><strong style={{ color: "#d1d5db" }}>90%</strong></span>
                  <span>ACTUAL<br /><strong style={{ color: "#22c55e" }}>87%</strong></span>
                </div>
              </div>

              <svg
                aria-hidden="true"
                viewBox="0 0 700 230"
                preserveAspectRatio="none"
                style={{
                  position: "absolute",
                  left: 118,
                  right: 20,
                  top: 300,
                  width: "calc(100% - 138px)",
                  height: 220,
                  zIndex: 0,
                  opacity: 0.42,
                  filter: "blur(0.45px)",
                }}
              >
                <path d="M504 0 C504 54 88 44 88 96" stroke="rgba(226,232,240,0.62)" strokeWidth="1.4" fill="none" />
                <path d="M504 0 C504 58 260 50 260 96" stroke="rgba(226,232,240,0.62)" strokeWidth="1.4" fill="none" />
                <path d="M504 0 C504 58 438 50 438 96" stroke="rgba(226,232,240,0.62)" strokeWidth="1.4" fill="none" />
                <path d="M504 0 C504 50 612 48 612 96" stroke="rgba(226,232,240,0.62)" strokeWidth="1.4" fill="none" />
              </svg>

              <div style={{ position: "absolute", left: 118, right: 20, top: 392, display: "grid", gridTemplateColumns: "repeat(4, minmax(104px, 1fr))", gap: 18, zIndex: 1, filter: "blur(0.45px)" }}>
                {miniKpis.map((kpi) => (
                  <div
                    key={kpi.title}
                    style={{
                      border: `1px solid ${kpi.accent}`,
                      borderRadius: 9,
                      padding: 12,
                      minHeight: 146,
                      background: "rgba(8,13,20,0.94)",
                      boxShadow: `0 0 22px ${kpi.accent}22`,
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 900 }}>{kpi.title}</div>
                    <div style={{ marginTop: 8, color: "#8b97a8", fontSize: 10 }}>Weekly</div>
                    <div style={{ marginTop: 8, border: "1px solid rgba(148,163,184,0.20)", borderRadius: 6, padding: "4px 6px", fontSize: 10 }}>Direct KPI</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12, fontSize: 10, color: "#8b97a8" }}>
                      <span>TARGET<br /><strong style={{ color: "#d1d5db" }}>{kpi.target}</strong></span>
                      <span>ACTUAL<br /><strong style={{ color: "#22c55e" }}>{kpi.actual}</strong></span>
                    </div>
                    <div style={{ marginTop: 12, borderRadius: 6, background: "rgba(148,163,184,0.10)", padding: "6px 7px", fontSize: 10, color: "#cbd5e1" }}>
                      {kpi.initiatives} initiative{kpi.initiatives === 1 ? "" : "s"}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ position: "absolute", left: 44, bottom: 34, display: "flex", gap: 12, alignItems: "center", color: "#cbd5e1" }}>
                <div style={{ width: 42, height: 42, border: "1px solid rgba(34,197,94,0.35)", borderRadius: 999, display: "grid", placeItems: "center", color: "#22c55e", fontWeight: 900 }}>✓</div>
                <div>
                  <div style={{ fontWeight: 900, fontSize: 14 }}>Built for enterprise teams</div>
                  <div style={{ color: "#8b97a8", fontSize: 13, marginTop: 3 }}>Secure. Scalable. Reliable.</div>
                </div>
              </div>
            </div>

            <form
              onSubmit={onLogin}
              style={{
                border: "1px solid rgba(148,163,184,0.26)",
                borderRadius: 22,
                padding: 34,
                background: "linear-gradient(145deg, rgba(15,23,42,0.92), rgba(8,13,20,0.92))",
                boxShadow: "0 28px 70px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.04)",
                display: "grid",
                gap: 18,
                maxWidth: 500,
                justifySelf: "center",
                width: "100%",
              }}
            >
              <div>
                <h2 style={{ margin: 0, fontSize: 28, lineHeight: 1.1 }}>Welcome back</h2>
                <p style={{ margin: "12px 0 0", color: "#aab4c2", fontSize: 15 }}>
                  Sign in to continue to your workspace.
                </p>
              </div>
              <label style={{ display: "grid", gap: 8, color: "#e5e7eb", fontSize: 13, fontWeight: 800 }}>
                Email
                <input
                  placeholder="you@example.com"
                  value={loginEmail}
                  onChange={(event) => setLoginEmail(event.target.value)}
                  style={darkInputStyle}
                />
              </label>
              <label style={{ display: "grid", gap: 8, color: "#e5e7eb", fontSize: 13, fontWeight: 800 }}>
                Password
                <input
                  placeholder="Enter your password"
                  type="password"
                  value={loginPassword}
                  onChange={(event) => setLoginPassword(event.target.value)}
                  style={darkInputStyle}
                />
              </label>
              <button
                disabled={isLoading}
                style={{
                  border: "1px solid #16a34a",
                  borderRadius: 10,
                  padding: "14px 16px",
                  background: isLoading ? "#14532d" : "linear-gradient(135deg, #16a34a, #047857)",
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 900,
                  cursor: isLoading ? "not-allowed" : "pointer",
                  boxShadow: "0 12px 26px rgba(22,163,74,0.24)",
                }}
              >
                {isLoading ? "Signing in..." : "Sign in →"}
              </button>
              {message ? (
                <div style={{ border: "1px solid rgba(96,165,250,0.32)", background: "rgba(37,99,235,0.12)", color: "#bfdbfe", borderRadius: 10, padding: 12, fontWeight: 800, fontSize: 13 }}>
                  {message}
                </div>
              ) : null}
              <div style={{ display: "flex", justifyContent: "center", gap: 10, color: "#8b97a8", fontSize: 13, marginTop: 6 }}>
                <span>Your data is secure.</span>
                <span style={{ color: "#22c55e", fontWeight: 800 }}>Contact support</span>
              </div>
            </form>
          </section>
        </div>
      </main>
      </>
    );
  }

  return (
    <main
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at 18% 14%, rgba(34,197,94,0.14), transparent 26%), radial-gradient(circle at 84% 10%, rgba(14,165,233,0.10), transparent 30%), linear-gradient(135deg, #05090f 0%, #081018 48%, #04070b 100%)",
        color: "#f8fafc",
        padding: "32px 28px",
        fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div style={{ maxWidth: 1180, margin: "0 auto", display: "grid", gap: 22 }}>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <img
              src={brandLogoUrl}
              alt="Lenses logo"
              style={{ width: 58, height: 58, objectFit: "contain", clipPath: "inset(3px 0 0 0)" }}
            />
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, color: "#22c55e", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Lenses CPM
              </div>
              <h1 style={{ margin: 0, fontSize: 30, lineHeight: 1.05 }}>
                Users & Permissions
              </h1>
              <p style={{ margin: "6px 0 0", color: "#aab4c2" }}>
                Create login accounts and assign scoped roles for access, ownership, and task updates.
              </p>
            </div>
          </div>
          <Link to="/" style={{ color: "#86efac", fontWeight: 900, textDecoration: "none" }}>
            Strategy Map
          </Link>
        </header>

        {message ? (
          <div style={{ border: "1px solid rgba(96,165,250,0.34)", background: "rgba(37,99,235,0.14)", color: "#bfdbfe", borderRadius: 10, padding: 12, fontWeight: 800 }}>
            {message}
          </div>
        ) : null}

        <section
          style={{
            background: "linear-gradient(145deg, rgba(15,23,42,0.88), rgba(8,13,20,0.92))",
            border: "1px solid rgba(148,163,184,0.24)",
            borderRadius: 16,
            padding: 18,
            display: "grid",
            gap: 12,
            boxShadow: "0 24px 60px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
        >
          <form
            onSubmit={onCreateUser}
            style={{
              display: "grid",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18 }}>Create User</h2>
                <p style={{ margin: "6px 0 0", color: "#9ca3af", fontSize: 13 }}>
                  Add an account, then assign enterprise, lens, or access tag permissions below.
                </p>
              </div>
              <button type="button" onClick={onLogout} style={{ ...buttonStyle, background: "rgba(15,23,42,0.72)", color: "#e5e7eb", border: "1px solid rgba(148,163,184,0.32)" }}>
                Sign out {currentUser.name}
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: 10 }}>
              <input placeholder="Name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} style={inputStyle} />
              <input placeholder="Email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} style={inputStyle} />
              <input placeholder="Temporary password" type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} style={inputStyle} />
              <select value={form.enterprise_id} onChange={(event) => setForm({ ...form, enterprise_id: event.target.value })} style={inputStyle}>
                <option value="">No default enterprise</option>
                {enterprises.map((enterprise) => <option key={enterprise.id} value={enterprise.id}>{enterprise.name}</option>)}
              </select>
            </div>
            <button disabled={isLoading} style={buttonStyle}>Create user</button>
          </form>
        </section>

        <section style={darkPanelStyle}>
          <button
            type="button"
            onClick={() => setIsRoleGuideExpanded((current) => !current)}
            style={{
              border: "none",
              background: "transparent",
              color: "#f8fafc",
              padding: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <span>
              <span style={{ display: "block", fontSize: 18, fontWeight: 950 }}>Role Guide</span>
              <span style={{ display: "block", marginTop: 4, color: "#9ca3af", fontSize: 13 }}>
                {isRoleGuideExpanded ? "Permission definitions and scope rules." : "Collapsed. Expand when you need role and scope definitions."}
              </span>
            </span>
            <span style={{ color: "#86efac", fontSize: 13, fontWeight: 950 }}>
              {isRoleGuideExpanded ? "Hide" : "Show"}
            </span>
          </button>
          {isRoleGuideExpanded ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                {roles.map((role) => (
                  <div key={role.key} style={{ border: "1px solid rgba(148,163,184,0.22)", borderRadius: 10, padding: 12, background: "rgba(15,23,42,0.66)" }}>
                    <div style={{ fontWeight: 900 }}>{role.name}</div>
                    <div style={{ color: "#cbd5e1", fontSize: 13, marginTop: 4 }}>{role.description}</div>
                    <div style={{ color: "#9ca3af", fontSize: 12, marginTop: 8, fontWeight: 700 }}>{roleScopeHint(role.key)}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
                {assignmentScopes.map((scope) => (
                  <div key={scope} style={{ border: "1px solid rgba(148,163,184,0.22)", borderRadius: 10, padding: 10, background: "rgba(2,6,12,0.34)" }}>
                    <div style={{ fontSize: 12, fontWeight: 900, textTransform: "uppercase", color: "#86efac" }}>{scope}</div>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>{scopeDescriptions[scope]}</div>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </section>

        <section style={{ ...darkPanelStyle, padding: 0, overflow: "hidden" }}>
          <div style={{ padding: 16, borderBottom: "1px solid rgba(148,163,184,0.22)", display: "grid", gap: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontWeight: 950, fontSize: 18 }}>People & Permissions</div>
                <div style={{ color: "#9ca3af", fontSize: 13, marginTop: 4 }}>
                  {filteredUsers.length} of {users.length} user{users.length === 1 ? "" : "s"} shown
                </div>
              </div>
              <input
                type="search"
                placeholder="Search users, owners, roles, scopes..."
                value={userSearch}
                onChange={(event) => setUserSearch(event.target.value)}
                style={{ ...inputStyle, minWidth: 280, maxWidth: 420 }}
              />
            </div>
          </div>
          {users.length === 0 ? (
            <div style={{ padding: 16, color: "#9ca3af" }}>No users loaded yet.</div>
          ) : filteredUsers.length === 0 ? (
            <div style={{ padding: 16, color: "#9ca3af" }}>No users match that search.</div>
          ) : (
            <div style={{ display: "grid", gap: 14, padding: 16 }}>
              {filteredUsers.map((user) => {
                const draft = editDrafts[user.id];
                const hasUserChanges = hasUnsavedUserChanges(user, draft);
                const ownershipSummary = ownershipByUserId[user.id] ?? emptyOwnershipSummary;
                const ownershipDescription = describeOwnership(ownershipSummary);
                const isKpiOwner = hasWorkspaceOwnership(ownershipSummary);
                const assignmentDraft = assignmentDrafts[user.id] ?? { role_key: "", scope: "", scope_id: "" };
                const canAddPermissions = Boolean(
                  assignmentDraft.role_key &&
                  assignmentDraft.scope &&
                  (scopesWithoutIds.has(assignmentDraft.scope) || assignmentDraft.scope_id)
                );
                const lensScope = parseLensScopeId(assignmentDraft.scope_id);
                const accessTagScope = parseAccessTagScopeId(assignmentDraft.scope_id);
                const lensEnterpriseOptions = enterprises.filter((enterprise) =>
                  (lensOptionsByEnterpriseId[String(enterprise.id)] ?? []).length > 0
                );
                const selectedLensOptions = lensOptionsByEnterpriseId[lensScope.enterpriseId] ?? [];
                const showOwnershipDetails = expandedOwnershipUserIds.has(user.id);
                return (
                  <div
                    key={user.id}
                    style={{
                      padding: 16,
                      border: "1px solid rgba(148,163,184,0.28)",
                      borderLeft: `5px solid ${user.is_active ? "#22c55e" : "#64748b"}`,
                      borderRadius: 14,
                      background:
                        "linear-gradient(135deg, rgba(15,23,42,0.92), rgba(8,13,20,0.86))",
                      boxShadow: "0 18px 42px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.04)",
                      display: "grid",
                      gap: 14,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <div style={{ fontSize: 18, fontWeight: 950 }}>{user.name || user.email}</div>
                          <span
                            style={{
                              border: `1px solid ${user.is_active ? "rgba(34,197,94,0.42)" : "rgba(148,163,184,0.32)"}`,
                              borderRadius: 999,
                              padding: "4px 8px",
                              color: user.is_active ? "#86efac" : "#cbd5e1",
                              background: user.is_active ? "rgba(34,197,94,0.12)" : "rgba(148,163,184,0.12)",
                              fontSize: 12,
                              fontWeight: 900,
                            }}
                          >
                            {user.is_active ? "Active" : "Inactive"}
                          </span>
                          {isKpiOwner ? (
                            <span
                              style={{
                                border: "1px solid rgba(34,197,94,0.36)",
                                borderRadius: 999,
                                padding: "4px 8px",
                                color: "#bbf7d0",
                                background: "rgba(34,197,94,0.10)",
                                fontSize: 12,
                                fontWeight: 900,
                              }}
                            >
                              Workspace owner
                            </span>
                          ) : null}
                        </div>
                        <div style={{ color: "#9ca3af", fontSize: 13, marginTop: 5 }}>
                          {user.email}
                          {user.enterprise_id ? ` · ${enterpriseNameById.get(String(user.enterprise_id)) ?? `Enterprise ${user.enterprise_id}`}` : ""}
                        </div>
                      </div>
                      {hasUserChanges ? (
                        <button
                          type="button"
                          disabled={isLoading}
                          onClick={() => onSaveUser(user)}
                          style={buttonStyle}
                        >
                          Save changes
                        </button>
                      ) : null}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: 10 }}>
                      <input
                        aria-label={`${user.name} name`}
                        value={draft?.name ?? user.name}
                        onChange={(event) => updateDraft(user.id, { name: event.target.value })}
                        style={inputStyle}
                      />
                      <input
                        aria-label={`${user.name} email`}
                        value={draft?.email ?? user.email}
                        onChange={(event) => updateDraft(user.id, { email: event.target.value })}
                        style={inputStyle}
                      />
                      <select
                        aria-label={`${user.name} default enterprise`}
                        value={draft?.enterprise_id ?? (user.enterprise_id == null ? "" : String(user.enterprise_id))}
                        onChange={(event) => updateDraft(user.id, { enterprise_id: event.target.value })}
                        style={inputStyle}
                      >
                        <option value="">No default enterprise</option>
                        {enterprises.map((enterprise) => <option key={enterprise.id} value={enterprise.id}>{enterprise.name}</option>)}
                      </select>
                      <input
                        aria-label={`${user.name} new password`}
                        placeholder="New password"
                        type="password"
                        value={draft?.password ?? ""}
                        onChange={(event) => updateDraft(user.id, { password: event.target.value })}
                        style={inputStyle}
                      />
                      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 900, color: "#e5e7eb" }}>
                        <input
                          type="checkbox"
                          checked={draft?.is_active ?? user.is_active}
                          onChange={(event) => updateDraft(user.id, { is_active: event.target.checked })}
                        />
                        Account active
                      </label>
                    </div>
                    <div
                      style={{
                        border: "1px solid rgba(148,163,184,0.22)",
                        borderRadius: 10,
                        padding: 12,
                        display: "grid",
                        gap: 10,
                        background: "rgba(2,6,12,0.34)",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                        <div>
                          <div style={{ fontWeight: 900 }}>Access Grants</div>
                          <div style={{ fontSize: 12, color: "#9ca3af" }}>
                            Add enterprise access, specific lens access, and sensitive field access tags.
                          </div>
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <div style={{ width: "100%", fontSize: 12, color: "#9ca3af", fontWeight: 800 }}>
                          Add permissions
                        </div>
                          <select
                            aria-label={`${user.name} assignment role`}
                            value={assignmentDraft.role_key}
                            onChange={(event) => updateAssignmentDraft(user.id, { role_key: event.target.value })}
                            style={compactInputStyle}
                            title={roleDescriptionByKey.get(assignmentDraft.role_key) ?? ""}
                          >
                            <option value="">Choose role</option>
                            {assignableAccessRoles.map((role) => (
                              <option key={role.key} value={role.key}>
                                {role.name}
                              </option>
                            ))}
                          </select>
                          <select
                            aria-label={`${user.name} assignment scope`}
                            value={assignmentDraft.scope}
                            onChange={(event) => {
                              const nextScope = event.target.value;
                              updateAssignmentDraft(user.id, {
                                scope: nextScope,
                                scope_id:
                                  scopesWithoutIds.has(nextScope)
                                    ? ""
                                    : "",
                              });
                            }}
                            style={compactInputStyle}
                          >
                            <option value="">Choose scope</option>
                            {assignmentScopes.map((scope) => (
                              <option key={scope} value={scope}>
                                {scope}
                              </option>
                            ))}
                          </select>
                          {assignmentDraft.scope === "enterprise" ? (
                            <select
                              aria-label={`${user.name} assignment enterprise`}
                              value={assignmentDraft.scope_id}
                              onChange={(event) => updateAssignmentDraft(user.id, { scope_id: event.target.value })}
                              style={compactInputStyle}
                            >
                              <option value="">Select enterprise</option>
                              {enterprises.map((enterprise) => (
                                <option key={enterprise.id} value={enterprise.id}>
                                  {enterprise.name}
                                </option>
                              ))}
                            </select>
                          ) : assignmentDraft.scope === "lens" ? (
                            <>
                              <select
                                aria-label={`${user.name} lens enterprise`}
                                value={lensScope.enterpriseId}
                                onChange={(event) => {
                                  const nextEnterpriseId = event.target.value;
                                  const firstLensId = lensOptionsByEnterpriseId[nextEnterpriseId]?.[0]?.id ?? "";
                                  updateAssignmentDraft(user.id, {
                                    scope_id: buildLensScopeId(nextEnterpriseId, firstLensId),
                                  });
                                }}
                                style={compactInputStyle}
                              >
                                <option value="">Select enterprise</option>
                                {lensEnterpriseOptions.map((enterprise) => (
                                  <option key={enterprise.id} value={enterprise.id}>
                                    {enterprise.name}
                                  </option>
                                ))}
                              </select>
                              <select
                                aria-label={`${user.name} assignment lens`}
                                value={lensScope.lensId}
                                onChange={(event) =>
                                  updateAssignmentDraft(user.id, {
                                    scope_id: buildLensScopeId(lensScope.enterpriseId, event.target.value),
                                  })
                                }
                                disabled={!lensScope.enterpriseId || selectedLensOptions.length === 0}
                                style={compactInputStyle}
                              >
                                <option value="">Select lens</option>
                                {selectedLensOptions.map((lens) => (
                                  <option key={lens.id} value={lens.id}>
                                    {lens.name}
                                  </option>
                                ))}
                              </select>
                            </>
                          ) : assignmentDraft.scope === "access_tag" ? (
                            <>
                              <select
                                aria-label={`${user.name} access tag enterprise`}
                                value={accessTagScope.enterpriseId}
                                onChange={(event) => {
                                  const nextEnterpriseId = event.target.value;
                                  const nextTag = accessTagScope.tag || accessTagOptions[0].key;
                                  updateAssignmentDraft(user.id, {
                                    scope_id: buildAccessTagScopeId(nextEnterpriseId, nextTag),
                                  });
                                }}
                                style={compactInputStyle}
                              >
                                <option value="">Select enterprise</option>
                                {enterprises.map((enterprise) => (
                                  <option key={enterprise.id} value={enterprise.id}>
                                    {enterprise.name}
                                  </option>
                                ))}
                              </select>
                              <select
                                aria-label={`${user.name} access tag`}
                                value={accessTagScope.tag}
                                onChange={(event) =>
                                  updateAssignmentDraft(user.id, {
                                    scope_id: buildAccessTagScopeId(accessTagScope.enterpriseId, event.target.value),
                                  })
                                }
                                disabled={!accessTagScope.enterpriseId}
                                style={compactInputStyle}
                              >
                                <option value="">Select access tag</option>
                                {accessTagOptions.map((tag) => (
                                  <option key={tag.key} value={tag.key}>
                                    {tag.label}
                                  </option>
                                ))}
                              </select>
                            </>
                          ) : (
                            <input
                              aria-label={`${user.name} assignment scope id`}
                              placeholder={!assignmentDraft.scope || scopesWithoutIds.has(assignmentDraft.scope) ? "No scope id" : "Scope id"}
                              disabled={!assignmentDraft.scope || scopesWithoutIds.has(assignmentDraft.scope)}
                              value={assignmentDraft.scope_id}
                              onChange={(event) => updateAssignmentDraft(user.id, { scope_id: event.target.value })}
                              style={compactInputStyle}
                            />
                          )}
                          <button
                            type="button"
                            disabled={isLoading || !canAddPermissions}
                            onClick={() => onAddRoleAssignment(user)}
                            style={{
                              ...smallButtonStyle,
                              opacity: isLoading || !canAddPermissions ? 0.55 : 1,
                              cursor: isLoading || !canAddPermissions ? "not-allowed" : "pointer",
                            }}
                          >
                            Add permissions
                          </button>
                      </div>

                      {(roleAssignments[user.id] ?? []).length === 0 ? (
                        <div style={{ fontSize: 13, color: "#9ca3af" }}>No access grants assigned.</div>
                      ) : (
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {(roleAssignments[user.id] ?? []).map((assignment) => (
                            <span
                              key={assignment.id}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                border: "1px solid rgba(148,163,184,0.32)",
                                borderRadius: 999,
                                padding: "6px 8px 6px 10px",
                                background: "rgba(15,23,42,0.74)",
                                color: "#e5e7eb",
                                fontSize: 12,
                                fontWeight: 800,
                              }}
                              title={roleKeyById.get(assignment.role_id) ?? ""}
                            >
                              {roleNameById.get(assignment.role_id) ?? `Role ${assignment.role_id}`} · {formatScope(assignment)}
                              <button
                                type="button"
                                onClick={() => onDeleteRoleAssignment(assignment.id)}
                                disabled={isLoading}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: "#fca5a5",
                                  cursor: "pointer",
                                  fontWeight: 900,
                                  padding: 0,
                                }}
                              >
                                Remove
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      {isKpiOwner ? (
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                          <span style={{ color: "#86efac", fontSize: 12, fontWeight: 900 }}>
                            KPI Owner through workspace ownership
                          </span>
                          <button
                            type="button"
                            onClick={() => toggleOwnershipDetails(user.id)}
                            style={{
                              border: "1px solid rgba(148,163,184,0.32)",
                              borderRadius: 999,
                              background: "rgba(15,23,42,0.7)",
                              color: "#e5e7eb",
                              padding: "5px 9px",
                              fontSize: 12,
                              fontWeight: 900,
                              cursor: "pointer",
                            }}
                          >
                            {showOwnershipDetails ? "Hide ownership details" : "Show ownership details"}
                          </button>
                        </div>
                      ) : null}
                      {showOwnershipDetails ? (
                        <div style={{ borderTop: "1px solid rgba(148,163,184,0.22)", paddingTop: 10, display: "grid", gap: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                          <div style={{ fontWeight: 900 }}>Workspace Ownership</div>
                          <div style={{ color: ownershipDescription ? "#86efac" : "#9ca3af", fontSize: 12, fontWeight: 800 }}>
                            {ownershipDescription || "No owned KPIs, initiatives, or tasks found"}
                          </div>
                        </div>
                        {ownershipDescription ? (
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {[
                              ...ownershipSummary.kpis.slice(0, 3).map((label) => `KPI: ${label}`),
                              ...ownershipSummary.initiatives.slice(0, 2).map((label) => `Initiative: ${label}`),
                              ...ownershipSummary.tasks.slice(0, 2).map((label) => `Task: ${label}`),
                            ].map((label) => (
                              <span
                                key={label}
                                style={{
                                  border: "1px solid rgba(34,197,94,0.32)",
                                  borderRadius: 999,
                                  background: "rgba(34,197,94,0.12)",
                                  color: "#bbf7d0",
                                  padding: "5px 8px",
                                  fontSize: 12,
                                  fontWeight: 800,
                                }}
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

const inputStyle: React.CSSProperties = {
  border: "1px solid rgba(148,163,184,0.32)",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  background: "rgba(2,6,12,0.46)",
  color: "#f8fafc",
  outline: "none",
};

const darkInputStyle: React.CSSProperties = {
  border: "1px solid rgba(148,163,184,0.32)",
  borderRadius: 10,
  padding: "14px 14px",
  fontSize: 15,
  background: "rgba(2,6,12,0.42)",
  color: "#f8fafc",
  outline: "none",
};

const compactInputStyle: React.CSSProperties = {
  ...inputStyle,
  padding: "8px 10px",
  fontSize: 13,
};

const buttonStyle: React.CSSProperties = {
  border: "1px solid rgba(34,197,94,0.55)",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  fontWeight: 900,
  background: "linear-gradient(135deg, rgba(34,197,94,0.92), rgba(4,120,87,0.92))",
  color: "#fff",
  cursor: "pointer",
  boxShadow: "0 12px 26px rgba(22,163,74,0.20)",
};

const smallButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  padding: "8px 10px",
  fontSize: 13,
};

const darkPanelStyle: React.CSSProperties = {
  background: "linear-gradient(145deg, rgba(15,23,42,0.88), rgba(8,13,20,0.92))",
  border: "1px solid rgba(148,163,184,0.24)",
  borderRadius: 16,
  padding: 18,
  display: "grid",
  gap: 16,
  boxShadow: "0 24px 60px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.04)",
};
